import * as React from "react";
import { observer } from "mobx-react";
import { UitkImage, UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkCardAloha, UitkCardAlohaContentSection, UitkCardAlohaLink } from "uitk-react-cards";
import { DestinationSuggestionsProps } from "../typings";
import { Destination } from "src/typings/microserviceModels/destination-suggestions-flex-module.d.ts";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { IsomorphicCarousel } from "components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { useLocalization } from "@shared-ui/localization-context";
import { withMabTracker, MabTrackEvent } from "components/utility/analytics/MabTracker";
import { UitkHeading, UitkSubheading } from "uitk-react-text";
import { UitkSpacing } from "uitk-react-spacing";

const EmbeddingRedesignCard = withMabTracker(
  ({
    destination,
    cardIndex,
    mabTrack,
  }: {
    destination: Destination;
    cardIndex: number;
    mabTrack?: (x: MabTrackEvent) => null;
  }) => {
    const rfrrId = cardIndex + 1;

    const cardClicked = mabTrack
      ? () => {
          const mabTrackData: MabTrackEvent = {
            moduleName: "embedding",
            eventTarget: "embedding",
            eventType: "homepageClick.nonPath",
            rfrr: rfrrId.toString(),
          };
          mabTrack(mabTrackData);
        }
      : undefined;

    return (
      <>
        <UitkCardAloha className="EmbeddingCard uitk-flex-justify-content-flex-start uitk-flex-column">
          <UitkCardAlohaContentSection padded={false}>
            <UitkFigure ratio={UitkFigureAspectRatioType.R16_9} className="uitk-card-aloha-roundcorner-all">
              <UitkImage src={destination.destinationImg} alt={destination.imgAlt} />
            </UitkFigure>
            <UitkCardAlohaLink>
              <TrackedLink
                moduleName="Embedding"
                action={Action.CLICK}
                href={destination.url}
                rfrr={rfrrId}
                onClick={cardClicked}
              >
                {destination.name}
              </TrackedLink>
            </UitkCardAlohaLink>
          </UitkCardAlohaContentSection>
          <UitkSpacing padding={{ blockstart: "one" }}>
            <UitkHeading level={3} size={400}>
              {destination.name}
            </UitkHeading>
          </UitkSpacing>
        </UitkCardAloha>
      </>
    );
  }
);

export const EmbeddingRedesign = observer(({ props }: { props: DestinationSuggestionsProps }) => {
  const { id, fmId, model } = props.flexComponent;
  const keyHelper = new ItemKeyHelper(id);
  const { formatText } = useLocalization();
  const title = formatText("destinationSuggestions.embedding.title.v2");
  const subtitle = formatText("destinationSuggestions.embedding.title");

  return (
    <UitkSpacing padding={{ blockend: "three" }}>
      <div data-testid="embeddingRedesign" id={id} data-fm={fmId}>
        <UitkSpacing padding={{ blockend: "three", blockstart: "three" }}>
          <div>
            <UitkHeading level={2} size={700}>
              {title}
            </UitkHeading>
            <UitkSubheading level={3}>{subtitle}</UitkSubheading>
          </div>
        </UitkSpacing>
        <IsomorphicCarousel
          className="carouselComponent"
          itemsVisible={{
            lg: 4,
            md: 4,
            sm: 2,
          }}
          buttonText={{
            nextButton: formatText("carousel.item.next"),
            prevButton: formatText("carousel.item.prev"),
          }}
          pageBy={1}
          peekWidth={1}
        >
          {model.destinations.map((destination, index) => {
            return (
              <div key={keyHelper.next()}>
                <EmbeddingRedesignCard destination={destination} cardIndex={index} mabTrack={props.mabTrack} />
              </div>
            );
          })}
        </IsomorphicCarousel>
      </div>
    </UitkSpacing>
  );
});
(EmbeddingRedesign as React.SFC).displayName = "EmbeddingRedesignView";
